// ResourceMentionChip — a typed @-mention rendered as a pill, pure.
//
// Promoted from sketches/chat/RefChip.tsx: a kind glyph + the resource
// title, reading as one atom the eye parses as "a reference," not a word.
// Used both as a REMOVABLE draft chip (the @ menu's pick, riding in
// PowerComposer's context row before send) and, by a caller downstream, as
// a static sent chip — `onRemove` present ⇒ removable, absent ⇒ static.
//
// The mentioned resource is named `resourceRef`, never `ref` — React
// reserves the JSX attribute name `ref` on EVERY component, stripping it
// out of `props` before the component body ever runs; a prop literally
// called `ref` silently reads as `undefined` at the call site. This is the
// exact crash class RefChip.tsx once shipped with (fixed 2026-08-19); this
// promoted copy keeps the fix.

import { Trans } from "@lingui/react/macro";
import { Icon, type IconName } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden.js";
import type {
    ComposerAttachmentStatus,
    ComposerResourceKind,
    ComposerResourceRef,
} from "./types.js";
import "./ResourceMentionChip.css";

export type ResourceMentionChipProps = {
    readonly resourceRef: ComposerResourceRef;
    // Present ⇒ removable draft chip; absent ⇒ static sent chip.
    readonly onRemove?: () => void;
    // A short, REAL hover description (native `title` attribute) — e.g. the
    // resource kind noun. Never fabricated metadata (no size/page-count the
    // caller doesn't actually have); absent ⇒ no tooltip.
    readonly description?: string;
};

// One glyph per referenceable kind. `package` has no dedicated icon yet, so
// it borrows the folder glyph — a stand-in, flagged (types.ts carries the
// same note).
const kindGlyph = (kind: ComposerResourceKind): IconName => {
    switch (kind) {
        case "source":
            return "source";
        case "material":
            return "material";
        case "package":
            return "folder";
        case "file":
            return "file";
    }
};

// The screen-reader prefix, one whole phrase per kind (G14): interpolating the
// raw `kind` id into one msgid read "Referens till source:" in Swedish.
const ReferencePrefix = ({ kind }: { readonly kind: ComposerResourceKind }) => {
    switch (kind) {
        case "source":
            return <Trans>Reference to a source:</Trans>;
        case "material":
            return <Trans>Reference to a material:</Trans>;
        case "package":
            return <Trans>Reference to a package:</Trans>;
        case "file":
            return <Trans>Reference to a file:</Trans>;
    }
};

// The lifecycle indicator that rides on an optimistic chip (F83): a quiet
// pulsing dot while the file is uploading or its scanned pages are being read,
// a caution glyph on a failed upload or read. `ready` and a status-free chip render nothing extra (the
// resting chip is the confirmation). Colour is never the sole signal — a hidden
// label names the state for a screen reader.
const StatusIndicator = ({
    status,
}: {
    readonly status: ComposerAttachmentStatus;
}) => {
    switch (status) {
        case "uploading":
            return (
                <span
                    className="bilda-composer-chip__status"
                    data-status="uploading"
                    aria-hidden="true"
                />
            );
        case "reading":
            return (
                <span
                    className="bilda-composer-chip__status"
                    data-status="reading"
                    aria-hidden="true"
                />
            );
        case "failed":
        case "read_failed":
            return (
                <span
                    className="bilda-composer-chip__status"
                    data-status={status}
                    aria-hidden="true"
                >
                    <Icon name="warning" size="xs" />
                </span>
            );
        case "ready":
            return null;
    }
};

// The hidden, localized read-back of a chip's lifecycle (announced to a screen
// reader, never shown). Absent for a resting/ready chip.
const StatusLabel = ({
    status,
}: {
    readonly status: ComposerAttachmentStatus;
}) => {
    switch (status) {
        case "uploading":
            return (
                <VisuallyHidden>
                    {" "}
                    <Trans>uploading</Trans>
                </VisuallyHidden>
            );
        case "failed":
            return (
                <VisuallyHidden>
                    {" "}
                    <Trans>upload failed</Trans>
                </VisuallyHidden>
            );
        case "reading":
            return (
                <VisuallyHidden>
                    {" "}
                    <Trans>reading scanned pages</Trans>
                </VisuallyHidden>
            );
        case "read_failed":
            return (
                <VisuallyHidden>
                    {" "}
                    <Trans>could not be read</Trans>
                </VisuallyHidden>
            );
        case "ready":
            return null;
    }
};

export const ResourceMentionChip = ({
    resourceRef,
    onRemove,
    description,
}: ResourceMentionChipProps) => (
    <span
        className="bilda-composer-chip"
        data-kind={resourceRef.kind}
        data-status={resourceRef.status}
        title={description}
    >
        <span className="bilda-composer-chip__glyph" aria-hidden="true">
            <Icon name={kindGlyph(resourceRef.kind)} size="xs" />
        </span>
        {resourceRef.status !== undefined ? (
            <StatusIndicator status={resourceRef.status} />
        ) : null}
        <VisuallyHidden>
            <ReferencePrefix kind={resourceRef.kind} />{" "}
        </VisuallyHidden>
        <Text
            as="span"
            size="sm"
            weight="medium"
            className="bilda-composer-chip__title"
        >
            {resourceRef.title}
        </Text>
        {resourceRef.status !== undefined ? (
            <StatusLabel status={resourceRef.status} />
        ) : null}
        {onRemove !== undefined ? (
            <button
                type="button"
                className="bilda-composer-chip__remove"
                onClick={onRemove}
            >
                <VisuallyHidden>
                    <Trans>Remove reference {resourceRef.title}</Trans>
                </VisuallyHidden>
                <Icon name="close" size="xs" />
            </button>
        ) : null}
    </span>
);
